import { useCallback, useEffect, useMemo, useState } from 'react';

export interface PersistentColumnConfig<TKey extends string = string> {
  key: TKey;
  defaultVisible?: boolean;
  alwaysVisible?: boolean;
  width?: number;
}

export interface PersistentColumnPrefs<TKey extends string = string> {
  order: TKey[];
  visible: TKey[];
}

export type PersistentColumnWidths<TKey extends string = string> = Partial<Record<TKey, number>>;

interface UsePersistentColumnsOptions<TKey extends string> {
  columns: PersistentColumnConfig<TKey>[];
  storageKey: string;
  prefsSuffix?: string;
  widthsSuffix?: string;
  defaultVisibleKeys?: TKey[];
  minVisibleColumns?: number;
}

function canUseStorage() {
  return typeof localStorage !== 'undefined';
}

function readJson<T>(key: string, fallback: T): T {
  if (!canUseStorage()) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* no-op */
  }
}

function moveItem<T>(items: T[], fromIndex: number, toIndex: number) {
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return items;
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  if (item === undefined) return items;
  next.splice(toIndex, 0, item);
  return next;
}

function arraysEqual<T>(left: T[], right: T[]) {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

function prefsEqual<TKey extends string>(
  left: PersistentColumnPrefs<TKey>,
  right: PersistentColumnPrefs<TKey>,
) {
  return arraysEqual(left.order, right.order) && arraysEqual(left.visible, right.visible);
}

function widthsEqual<TKey extends string>(
  left: PersistentColumnWidths<TKey>,
  right: PersistentColumnWidths<TKey>,
) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every((key) => left[key as TKey] === right[key as TKey])
  );
}

export function buildPersistentColumnPrefs<TKey extends string>(
  columns: PersistentColumnConfig<TKey>[],
  defaultVisibleKeys?: TKey[],
): PersistentColumnPrefs<TKey> {
  const order = columns.map((column) => column.key);
  const visible =
    defaultVisibleKeys ??
    columns.filter((column) => column.defaultVisible !== false).map((column) => column.key);
  const alwaysVisible = columns
    .filter((column) => column.alwaysVisible)
    .map((column) => column.key);
  const visibleWithRequired = Array.from(new Set([...visible, ...alwaysVisible])).filter((key) =>
    order.includes(key),
  );

  return {
    order,
    visible: visibleWithRequired.length > 0 ? visibleWithRequired : order,
  };
}

export function reconcilePersistentColumnPrefs<TKey extends string>(
  prefs: Partial<PersistentColumnPrefs<TKey>> | null | undefined,
  columns: PersistentColumnConfig<TKey>[],
  defaultVisibleKeys?: TKey[],
): PersistentColumnPrefs<TKey> {
  const fallback = buildPersistentColumnPrefs(columns, defaultVisibleKeys);
  const ids = fallback.order;
  const storedOrder = Array.isArray(prefs?.order) ? prefs.order : fallback.order;
  const storedVisible = Array.isArray(prefs?.visible) ? prefs.visible : fallback.visible;
  const alwaysVisible = columns
    .filter((column) => column.alwaysVisible)
    .map((column) => column.key);
  const order = [
    ...storedOrder.filter((id) => ids.includes(id)),
    ...ids.filter((id) => !storedOrder.includes(id)),
  ];
  // A column the stored preference has never heard of is NEW — added to the
  // table since this user last saved their columns — and it defaults to shown
  // like it would for anyone else. Filtering visibility against the stored list
  // alone hid every column added after a preference existed, from exactly the
  // people who use the table most: the feature shipped, and the only users who
  // saw it were the ones who had never touched the column picker.
  const isNew = (id: TKey) => !storedOrder.includes(id) && !storedVisible.includes(id);
  const newlyAdded = ids.filter((id) => isNew(id) && fallback.visible.includes(id));
  const visible = storedVisible.filter((id) => ids.includes(id));
  const visibleWithRequired = Array.from(
    new Set([
      ...(visible.length > 0 ? visible : fallback.visible),
      ...newlyAdded,
      ...alwaysVisible,
    ]),
  );

  return { order, visible: visibleWithRequired };
}

export function buildPersistentColumnWidths<TKey extends string>(
  columns: PersistentColumnConfig<TKey>[],
): PersistentColumnWidths<TKey> {
  return columns.reduce<PersistentColumnWidths<TKey>>((acc, column) => {
    if (column.width) acc[column.key] = column.width;
    return acc;
  }, {});
}

export function usePersistentColumns<TKey extends string>({
  columns,
  storageKey,
  prefsSuffix = 'columns',
  widthsSuffix = 'widths',
  defaultVisibleKeys,
  minVisibleColumns = 1,
}: UsePersistentColumnsOptions<TKey>) {
  const prefsKey = `${storageKey}-${prefsSuffix}`;
  const widthsKey = `${storageKey}-${widthsSuffix}`;
  const defaultPrefs = useMemo(
    () => buildPersistentColumnPrefs(columns, defaultVisibleKeys),
    [columns, defaultVisibleKeys],
  );
  const defaultWidths = useMemo(() => buildPersistentColumnWidths(columns), [columns]);
  const [columnPrefs, setColumnPrefs] = useState<PersistentColumnPrefs<TKey>>(() =>
    reconcilePersistentColumnPrefs(readJson(prefsKey, defaultPrefs), columns, defaultVisibleKeys),
  );
  const [columnWidths, setColumnWidths] = useState<PersistentColumnWidths<TKey>>(() =>
    readJson(widthsKey, defaultWidths),
  );

  useEffect(() => {
    setColumnPrefs((current) => {
      const next = reconcilePersistentColumnPrefs(current, columns, defaultVisibleKeys);
      if (prefsEqual(current, next)) return current;
      writeJson(prefsKey, next);
      return next;
    });
    setColumnWidths((current) => {
      const next = { ...defaultWidths, ...current };
      return widthsEqual(current, next) ? current : next;
    });
  }, [columns, defaultVisibleKeys, defaultWidths, prefsKey]);

  const persistWidths = useCallback(
    (widths?: PersistentColumnWidths<TKey>) => {
      writeJson(widthsKey, widths ?? columnWidths);
    },
    [columnWidths, widthsKey],
  );

  const updatePrefs = useCallback(
    (updater: (current: PersistentColumnPrefs<TKey>) => PersistentColumnPrefs<TKey>) => {
      setColumnPrefs((current) => {
        const next = updater(current);
        if (next === current) return current;
        writeJson(prefsKey, next);
        return next;
      });
    },
    [prefsKey],
  );

  const toggleColumn = useCallback(
    (key: TKey) => {
      const column = columns.find((candidate) => candidate.key === key);
      if (column?.alwaysVisible) return;
      updatePrefs((current) => {
        const isVisible = current.visible.includes(key);
        if (isVisible && current.visible.length <= minVisibleColumns) return current;
        return {
          ...current,
          visible: isVisible
            ? current.visible.filter((visibleKey) => visibleKey !== key)
            : [...current.visible, key],
        };
      });
    },
    [columns, minVisibleColumns, updatePrefs],
  );

  const reorderColumns = useCallback(
    (activeKey: TKey, overKey: TKey) => {
      if (activeKey === overKey) return;
      updatePrefs((current) => ({
        ...current,
        order: moveItem(
          current.order,
          current.order.indexOf(activeKey),
          current.order.indexOf(overKey),
        ),
      }));
    },
    [updatePrefs],
  );

  const reorderVisibleColumns = useCallback(
    (activeKey: TKey, overKey: TKey) => {
      if (activeKey === overKey) return;
      updatePrefs((current) => {
        const visibleOrder = current.order.filter((key) => current.visible.includes(key));
        const newVisibleOrder = moveItem(
          visibleOrder,
          visibleOrder.indexOf(activeKey),
          visibleOrder.indexOf(overKey),
        );
        const hiddenOrder = current.order.filter((key) => !current.visible.includes(key));
        return { ...current, order: [...newVisibleOrder, ...hiddenOrder] };
      });
    },
    [updatePrefs],
  );

  const resetColumns = useCallback(() => {
    setColumnPrefs(defaultPrefs);
    setColumnWidths(defaultWidths);
    writeJson(prefsKey, defaultPrefs);
    writeJson(widthsKey, defaultWidths);
  }, [defaultPrefs, defaultWidths, prefsKey, widthsKey]);

  return {
    prefsKey,
    widthsKey,
    defaultPrefs,
    defaultWidths,
    columnPrefs,
    setColumnPrefs,
    columnWidths,
    setColumnWidths,
    toggleColumn,
    reorderColumns,
    reorderVisibleColumns,
    resetColumns,
    persistWidths,
  };
}
